/**
 * useDocumentMeta
 *
 * Dynamically updates <title> and Open Graph / Twitter meta tags for the
 * current page. Modern social crawlers (iMessage, WhatsApp, Twitter/X,
 * Discord) render JS before scraping metadata, so updating the DOM head
 * from a React effect meaningfully improves share previews on shared
 * public routes (wishlists, keepsakes, gift questionnaires), where the
 * static index.html tags would otherwise show a generic home-page card.
 *
 * Usage:
 *   useDocumentMeta({
 *     title: `${name}'s Wishlist, Mementa`,
 *     description: 'Gift ideas curated with love.',
 *     image: 'https://.../cover.jpg',
 *     url: window.location.href,
 *   });
 */
import { useEffect } from 'react';

const DEFAULT_IMAGE = `${window.location.origin}/og-cover.png`;

function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [key, val] = selector
      .replace('meta[', '')
      .replace(']', '')
      .split('=');
    el.setAttribute(key, val.replace(/"/g, ''));
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function useDocumentMeta({
  title,
  description,
  image,
  url,
  type = 'website',
} = {}) {
  useEffect(() => {
    // Remember originals so we can restore them on unmount (prevents
    // dynamic per-page meta from leaking into subsequent navigations).
    const original = {
      title: document.title,
      description: document.head
        .querySelector('meta[name="description"]')
        ?.getAttribute('content'),
      ogTitle: document.head
        .querySelector('meta[property="og:title"]')
        ?.getAttribute('content'),
      ogDescription: document.head
        .querySelector('meta[property="og:description"]')
        ?.getAttribute('content'),
      ogImage: document.head
        .querySelector('meta[property="og:image"]')
        ?.getAttribute('content'),
      ogUrl: document.head
        .querySelector('meta[property="og:url"]')
        ?.getAttribute('content'),
      ogType: document.head
        .querySelector('meta[property="og:type"]')
        ?.getAttribute('content'),
      twTitle: document.head
        .querySelector('meta[name="twitter:title"]')
        ?.getAttribute('content'),
      twDescription: document.head
        .querySelector('meta[name="twitter:description"]')
        ?.getAttribute('content'),
      twImage: document.head
        .querySelector('meta[name="twitter:image"]')
        ?.getAttribute('content'),
      canonical: document.head
        .querySelector('link[rel="canonical"]')
        ?.getAttribute('href'),
    };

    if (title) document.title = title;
    if (description) {
      setMeta('meta[name="description"]', 'content', description);
    }

    const resolvedImage = image || DEFAULT_IMAGE;
    const resolvedUrl = url || window.location.href;

    setMeta('meta[property="og:type"]', 'content', type);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:image"]', 'content', resolvedImage);
    setMeta('meta[property="og:image:secure_url"]', 'content', resolvedImage);
    setMeta('meta[property="og:url"]', 'content', resolvedUrl);

    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', resolvedImage);

    setLink('canonical', resolvedUrl);

    return () => {
      // Restore originals so the home-page defaults come back cleanly.
      if (original.title) document.title = original.title;
      if (original.description) {
        setMeta('meta[name="description"]', 'content', original.description);
      }
      if (original.ogTitle) {
        setMeta('meta[property="og:title"]', 'content', original.ogTitle);
      }
      if (original.ogDescription) {
        setMeta(
          'meta[property="og:description"]',
          'content',
          original.ogDescription
        );
      }
      if (original.ogImage) {
        setMeta('meta[property="og:image"]', 'content', original.ogImage);
        setMeta(
          'meta[property="og:image:secure_url"]',
          'content',
          original.ogImage
        );
      }
      if (original.ogUrl) {
        setMeta('meta[property="og:url"]', 'content', original.ogUrl);
      }
      if (original.ogType) {
        setMeta('meta[property="og:type"]', 'content', original.ogType);
      }
      if (original.twTitle) {
        setMeta('meta[name="twitter:title"]', 'content', original.twTitle);
      }
      if (original.twDescription) {
        setMeta(
          'meta[name="twitter:description"]',
          'content',
          original.twDescription
        );
      }
      if (original.twImage) {
        setMeta('meta[name="twitter:image"]', 'content', original.twImage);
      }
      if (original.canonical) setLink('canonical', original.canonical);
    };
  }, [title, description, image, url, type]);
}
